import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { TasksKanbanCardCustomerProps } from '../types/interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { TasksKanbanRelatedToType } from '@shared/config'

const disciplineLabels: Record<Discipline, string> = {
  [Discipline.Math]: 'Math',
  [Discipline.ELA]: 'ELA',
}

interface TasksKanbanCardCustomerOptions {
  props: TasksKanbanCardCustomerProps
}

interface TasksKanbanCardCustomerReturn {
  refCustomerName: ComputedRef<string>
  refKidName: ComputedRef<string>
  refDisciplineLabel: ComputedRef<string | null>
  refSubscriptionLabel: ComputedRef<string>
  refSecondLineText: ComputedRef<string>
  refIsSubscription: ComputedRef<boolean>
  refHasSecondLine: ComputedRef<boolean>
}

export const useTasksKanbanCardCustomer = ({ props }: TasksKanbanCardCustomerOptions): TasksKanbanCardCustomerReturn => {
  // first line
  const refCustomerName = computed<string>(() => props.customerName ?? '')

  // second line (kid + discipline, or subscription)
  const refKidName = computed<string>(() => props.kidName ?? '')

  const refDisciplineLabel = computed<string | null>(() => {
    if (!props.discipline) return null

    return disciplineLabels[props.discipline] ?? null
  })

  const refSubscriptionLabel = computed<string>(() => {
    if (!props.subscriptionId) return ''

    return `Subscription #${props.subscriptionId}`
  })

  const refIsSubscription = computed<boolean>(() => props.relatedToType === TasksKanbanRelatedToType.Subscription)

  const refSecondLineText = computed<string>(() => {
    if (refIsSubscription.value) return refSubscriptionLabel.value

    return [refKidName.value, refDisciplineLabel.value].filter(Boolean).join(' - ')
  })

  const refHasSecondLine = computed<boolean>(() => !!refSecondLineText.value)

  return {
    refCustomerName,
    refKidName,
    refDisciplineLabel,
    refSubscriptionLabel,
    refSecondLineText,
    refIsSubscription,
    refHasSecondLine,
  }
}
